import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { CartLineModel, OrderLineModel, WishListLineModel } from "@insite/client-framework/Types/ApiModels";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TYPOGRAPHY } from "@tSurya/theme/design.surya";
import { buildCrossWebsiteProductDetailPath, normalizeSourceBrand } from "@tSurya/Common/Utilities/websiteUtils";
import { MGBW_CONFIGURATION_ITEM_NUMBER } from "@tSurya/Common/MgbwPropertyKeys";
import React, { useState } from "react";
import { css } from "styled-components";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";

interface OwnProps {
    product: CartLineModel | ProductContextModel | OrderLineModel | WishListLineModel;
    onClick?: React.EventHandler<React.MouseEvent>;
    extendedStyles?: ProductDescriptionStyles;
    showGVExtraDescription?: boolean;
    disableLink?: boolean;
    /**
     * When true, renders the GlobalViews-style secondary details (ERP number + variant size)
     * even when the product is not GlobalViews. Used for MGBW case goods so they match the GV layout.
     * Default value: false */
    showSecondaryDetails?: boolean;
}

type Props = OwnProps;

export interface ProductDescriptionStyles {
    wrapper?: InjectableCss;
    productDetailLink?: LinkPresentationProps;
    productDetailDescription?: TypographyProps;
    erpNumber?: TypographyProps;
    variantSize?: TypographyProps;
}

export const productDescriptionStyles: ProductDescriptionStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    productDetailLink: {
        css: css`
            width: 100%;
        `,
        typographyProps: {
            css: css`
                width: 100%;
                ${wrapInContainerStyles}
            `,
        },
    },
    productDetailDescription: {
        color: "#323132",
        css: css`
            font-size: 16px;
        `,
    },
    erpNumber: {
        color: "#545454",
        css: css`
            margin-top: 4px;
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: ${TYPOGRAPHY.weight.baseBold};

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: ${TYPOGRAPHY.size.smaller};
                        `,
                    ],
                    "max",
                )}
        `,
    },
    variantSize: {
        color: "#545454",
        css: css`
            margin-top: 4px;
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: ${TYPOGRAPHY.weight.baseBold};

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: ${TYPOGRAPHY.size.smaller};
                        `,
                    ],
                    "max",
                )}
        `,
    },
};

const ProductDescription = ({ product, onClick, extendedStyles, showGVExtraDescription = true, showSecondaryDetails = false, disableLink = false }: Props) => {
    const [styles] = useState(() => mergeToNew(productDescriptionStyles, extendedStyles));

    // Get website redirect URLs from settings (configured in Admin → System Settings → Website Redirect Settings)
    const suryaWebsiteUrl = useAppSelector(state => state.context.settings.properties.suryaWebsiteUrl as string) || "https://www.surya.com";
    const globalViewsWebsiteUrl = useAppSelector(state => state.context.settings.properties.globalViewsWebsiteUrl as string) || "https://globalviews.surya.com";
    const mgbwWebsiteUrl = useAppSelector(state => state.context.settings.properties.mgbwWebsiteUrl as string) || "https://mgbw.surya.com";

    const sourceBrand = "product" in product ? product.product.properties?.sourceBrand : product.properties?.sourceBrand;

    const normalizedSourceBrand = normalizeSourceBrand(sourceBrand) || "surya";
    const isNotSurya = normalizedSourceBrand !== "surya";

    // Get ERP number for display
    const erpNumber = "product" in product ? product.product.productNumber : "productErpNumber" in product ? product.productErpNumber : product.erpNumber;
    const configurationItemNumber =
        "product" in product ? product.product.properties?.[MGBW_CONFIGURATION_ITEM_NUMBER] : product.properties?.[MGBW_CONFIGURATION_ITEM_NUMBER];

    // Get parentProductErpNumber for the API call (contains the product title value needed)
    // Cart lines expose it as a field, wish list lines carry it in properties.
    const parentProductErpNumber =
        "product" in product
            ? null // ProductContextModel - not used in cart
            : "parentProductErpNumber" in product
              ? product.parentProductErpNumber
              : product.properties?.parentProductErpNumber || null;

    // Remove "_P" suffix if present (same logic as ProductDetailsPage)
    const contentParams = parentProductErpNumber?.endsWith("_P") ? parentProductErpNumber?.replace("_P", "") : parentProductErpNumber;

    const hasDescriptiveProductName = !("product" in product) && isNotSurya && !!product.productName && product.productName !== erpNumber;

    // Swatches already carry a descriptive productName. GlobalViews and MGBW case goods use the
    // ERP number as productName and carry their descriptive title in parentProductErpNumber.
    const defaultDescription =
        "product" in product
            ? product.product.productTitle
            : (hasDescriptiveProductName && product.productName) || product.shortDescription || ("description" in product ? product.description : "");

    const useParentProductDescription = !hasDescriptiveProductName && (isNotSurya || showSecondaryDetails);
    const description = useParentProductDescription && contentParams ? contentParams : defaultDescription;

    // Get the base product detail path and apply cross-website redirection
    const baseProductDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;
    const productDetailPath = buildCrossWebsiteProductDetailPath(baseProductDetailPath, sourceBrand, suryaWebsiteUrl, globalViewsWebsiteUrl, mgbwWebsiteUrl);

    // Check if the link is external (redirects to a different website)
    const isExternalRedirect = productDetailPath !== baseProductDetailPath;

    const variantSize = "product" in product ? product.product.properties?.variantSize : product.properties?.variantSize;

    const renderDetails = () => {
        if (!isNotSurya && !showSecondaryDetails) {
            return null;
        }
        // Avoid duplicating the title line when the ERP number is the same as the description
        const showErpNumber = !configurationItemNumber && erpNumber && erpNumber !== description;
        return (
            <>
                {showErpNumber && <Typography {...styles.erpNumber}>{erpNumber}</Typography>}
                {configurationItemNumber && <Typography {...styles.erpNumber}>{`Item #: ${configurationItemNumber}`}</Typography>}
                {variantSize && <Typography {...styles.variantSize}>{variantSize}</Typography>}
            </>
        );
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            {productDetailPath && !disableLink && (!("isActiveProduct" in product) || product.isActiveProduct) ? (
                <Link
                    {...styles.productDetailLink}
                    href={productDetailPath}
                    alt={`See product details for ${description}`}
                    onClick={onClick}
                    data-test-selector="productDescriptionLink"
                    target={isExternalRedirect ? "_blank" : "_self"}
                >
                    {description}
                </Link>
            ) : (
                <Typography {...styles.productDetailDescription}>{description}</Typography>
            )}
            {showGVExtraDescription && renderDetails()}
        </StyledWrapper>
    );
};

export default ProductDescription;
